import { useEffect, useState } from "react";
import { BACKEND_URL } from "../lib/api";

/**
 * Band logo: served from a PUBLIC endpoint so it renders in <img src> with no auth.
 * Shows the fallback children (typically a music icon) when no logo or 404.
 * Pass `bust` to force-refresh after upload.
 */
export const BandLogo = ({ bandId, className = "w-10 h-10 rounded-xl", fallback, bust }) => {
  const [errored, setErrored] = useState(false);
  useEffect(() => {
    setErrored(false);
  }, [bandId, bust]);

  if (!bandId || errored) {
    return fallback || null;
  }
  const src = `${BACKEND_URL}/api/bands/${bandId}/logo${bust ? `?v=${bust}` : ""}`;
  return (
    <img
      src={src}
      alt="Band logo"
      className={`${className} object-cover`}
      onError={() => setErrored(true)}
      data-testid={`band-logo-${bandId}`}
    />
  );
};

/**
 * Player photo: requires auth, so we fetch as blob via native fetch (axios + blob
 * responseType throws ugly XHR errors on 404 which spam the dev console).
 * Returns the fallback (typically initials) until loaded; renders nothing on 404.
 */
export const PlayerPhoto = ({ playerId, className = "w-10 h-10 rounded-full", fallback, bust }) => {
  const [src, setSrc] = useState(null);

  useEffect(() => {
    if (!playerId) return;
    let active = true;
    let objectUrl = null;
    const token = localStorage.getItem("token");
    fetch(`${BACKEND_URL}/api/players/${playerId}/photo`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(async (r) => {
        if (!r.ok) return null;
        return r.blob();
      })
      .then((blob) => {
        if (!active || !blob) {
          if (active) setSrc(null);
          return;
        }
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      })
      .catch(() => {
        if (active) setSrc(null);
      });
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [playerId, bust]);

  if (!src) {
    return fallback || null;
  }
  return (
    <img
      src={src}
      alt="Profile"
      className={`${className} object-cover`}
      data-testid={`player-photo-${playerId}`}
    />
  );
};
